import * as React from 'react';
import { AppProvider, type Navigation, type Session, type Router } from '@toolpad/core/AppProvider';
import { DashboardLayout, ThemeSwitcher } from '@toolpad/core/DashboardLayout';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { Button, Stack } from '@mui/material';
import { AdminNav } from './navigationLayout';
import SidebarFooterAccount from './sidebarFooterAccount';
import { theme } from '../../theme/theme';

const ROLE_HIERARCHY: Record<string, Navigation> = {
  Staff: AdminNav,
  Manager: AdminNav,
  Admin: AdminNav,
  Super: AdminNav,
};

// Map navigation segments to API groups
const ENVIRONMENT_GROUPS: Record<string, string> = {
  weather: 'weather',
  water: 'water',
  soil: 'soil',
};

export default function Toolbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loginWithRedirect, logout, isLoading } = useAuth0();
  const isFpmHost = window.location.hostname === 'fpm.odp.golf';
  const nextHost = isFpmHost ? 'odp.golf' : 'fpm.odp.golf';

  const toggleDomain = React.useCallback(() => {
    const { protocol } = window.location;
    window.location.href = `${protocol}//${nextHost}`;
  }, [nextHost]);

  const ToolbarActions = React.useCallback(() => (
    <Stack direction="row" spacing={1} alignItems="center">
      <Button
        variant="outlined"
        size="small"
        onClick={toggleDomain}
        sx={{
          color: '#FFFF00',
          borderColor: '#FFFF00',
          textTransform: 'none',
          '&:hover': { borderColor: '#FFFF00' },
        }}
      >
        {isFpmHost ? 'Go to odp.golf' : 'Go to fpm'}
      </Button>
      <ThemeSwitcher />
    </Stack>
  ), [isFpmHost, toggleDomain]);

  // Create the session object based on Auth0 state
  const session: Session | null = isAuthenticated
    ? {
        user: {
          name: user?.name || '',
          email: user?.email || '',
          image: user?.picture || '',
        },
      }
    : null;

  // Determine the user roles, default to 'Staff' if no roles are assigned
  const roles = React.useMemo(() => {
    const userRoles = user?.['https://odp.golf/roles'] || [];
    if (userRoles.length === 0) {
      console.log('No roles found, defaulting to Staff');
      return ['Staff'];
    }
    return userRoles;
  }, [user]);

  // Select navigation based on role hierarchy - memoized at the highest level
  const navigation = React.useMemo(() => {
    if (!isLoading && isAuthenticated) {
      for (const role of roles) {
        if (ROLE_HIERARCHY[role]) {
          console.log(`Assigned navigation for role ${role}`);
          return ROLE_HIERARCHY[role];
        }
      }
    }
    return [];
  }, [isLoading, isAuthenticated, roles]);

  // Create router object for AppProvider
  const router = React.useMemo<Router>(() => ({
    pathname: location.pathname,
    searchParams: new URLSearchParams(location.search),
    navigate: (url: string | URL) => {
      if (typeof url === 'string') {
        // Handle environment routes
        if (url.startsWith('/environment/')) {
          const segments = url.split('/');
          const segment = segments[segments.length - 1];
          const group = ENVIRONMENT_GROUPS[segment];
          if (group) {
            navigate(`/environment/${group}`);
          } else {
            navigate(url);
          }
        } else {
          navigate(url);
        }
      } else {
        navigate(url.pathname + url.search);
      }
    },
  }), [location.pathname, location.search, navigate]);

  // Authentication object for AppProvider
  const authentication = React.useMemo(() => {
    return {
      signIn: () => loginWithRedirect(),
      signOut: () => logout({ logoutParams: { returnTo: window.location.origin } }),
    };
  }, [loginWithRedirect, logout]);

  return (
    <AppProvider
      session={session}
      authentication={authentication}
      navigation={navigation}
      router={router}
      theme={theme}
      branding={{
        logo: <img src="/logos/ODP_Logo.png" alt="ODP logo" />,
        title: 'Omni Directional Power'
      }}
    >
      <DashboardLayout 
        slots={{ toolbarActions: ToolbarActions, toolbarAccount: () => null, sidebarFooter: SidebarFooterAccount }}
        defaultSidebarCollapsed={false}
      >
        <div
          style={{
            overflowY: 'auto',
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Outlet />
        </div>
      </DashboardLayout>
    </AppProvider>
  );
}



